import { useState, useEffect } from 'react';
import { message } from 'antd';
import { serviceDiscoveryService, ServiceDataFetchOptions } from '../../../services/serviceDiscoveryService';
import type { ServiceInfo } from '@ms/shared-auth';

export const useServiceDiscovery = () => {
  const [services, setServices] = useState<ServiceInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedService, setSelectedService] = useState<ServiceInfo | null>(null);
  const [fetchResult, setFetchResult] = useState<any>(null);
  const [fetching, setFetching] = useState(false);
  const [healthCheckLoading, setHealthCheckLoading] = useState<Record<string, boolean>>({});

  // 加载微服务列表
  const loadServices = async () => {
    setLoading(true);
    try {
      const data = await serviceDiscoveryService.getAllServices();
      setServices(data);
      message.success(`成功加载 ${data.length} 个微服务`);
    } catch (error: any) {
      message.error(error.message || '加载微服务列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadServices();
  }, []);

  // 选择微服务
  const handleSelectService = (service: ServiceInfo | null) => {
    setSelectedService(service);
    setFetchResult(null);
  };

  // 检查微服务健康状态
  const handleCheckHealth = async (serviceName: string) => {
    setHealthCheckLoading((prev) => ({ ...prev, [serviceName]: true }));
    try {
      const isOnline = await serviceDiscoveryService.checkServiceHealth(serviceName);
      if (isOnline) {
        message.success(`微服务 ${serviceName} 在线`);
      } else {
        message.warning(`微服务 ${serviceName} 离线或不可用`);
      }
      // 刷新服务列表以更新状态
      await loadServices();
    } catch (error: any) {
      message.error(`检查健康状态失败: ${error.message}`);
    } finally {
      setHealthCheckLoading((prev) => ({ ...prev, [serviceName]: false }));
    }
  };

  // 从微服务抓取数据
  const handleFetchData = async (options: ServiceDataFetchOptions) => {
    setFetching(true);
    setFetchResult(null);
    try {
      const result = await serviceDiscoveryService.fetchServiceData(options);
      setFetchResult(result);
      if (result.success) {
        message.success('数据抓取成功');
      } else {
        message.error(result.error || '数据抓取失败');
      }
    } catch (error: any) {
      message.error(error.message || '数据抓取失败');
      setFetchResult({
        success: false,
        error: error.message || '未知错误',
      });
    } finally {
      setFetching(false);
    }
  };

  // 清除缓存
  const handleClearCache = () => {
    serviceDiscoveryService.clearCache();
    message.success('缓存已清除');
    loadServices();
  };

  return {
    services,
    loading,
    selectedService,
    setSelectedService,
    fetchResult,
    fetching,
    healthCheckLoading,
    loadServices,
    handleSelectService,
    handleCheckHealth,
    handleFetchData,
    handleClearCache,
  };
};
